import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';

const TrainingReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const trainingData = location.state || {};
  
  const [loading, setLoading] = useState(true);
  const [reportGenerated, setReportGenerated] = useState(false);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    // Fetch real evaluation data from backend
    const fetchReport = async () => {
      const sessionId = trainingData.sessionId || trainingData.sessionData?.sessionId;
      
      if (sessionId) {
        try {
          const baseUrl = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';
          const response = await fetch(`${baseUrl}/live-interview/session/${sessionId}/report`);
          
          if (response.ok) {
            const data = await response.json();
            const evaluation = data.evaluation || {};
            
            setReportData({
              candidateName: data.candidate_name || trainingData.candidateName || 'Candidate',
              candidateEmail: data.candidate_email || trainingData.candidateEmail || '',
              jobTitle: data.job_title || trainingData.jobTitle || 'Position',
              duration: data.duration_minutes || trainingData.duration || 5,
              completedAt: data.completed_at ? new Date(data.completed_at).toLocaleString() : new Date().toLocaleString(),
              // Use real data from the API response - Use overall_percentage if available
              overallScore: data.evaluation?.overall_percentage || data.overall_score || (data.evaluation?.overall_score) || 0,
              grade: data.overall_grade || (data.evaluation?.overall_grade) || 'F',
              questionsAsked: data.evaluation?.questions_count || (data.transcript?.length ? Math.floor(data.transcript.length / 2) : 1),
              strengths: data.evaluation?.strengths || [
                'Session completed successfully',
                'Engaged in conversation with AI trainer'
              ],
              areasForImprovement: data.evaluation?.areas_for_improvement || [
                'Continue practicing to improve interview skills',
                'Focus on providing more detailed responses'
              ],
              skillsAssessed: data.evaluation?.detailed_scores ? Object.entries(data.evaluation.detailed_scores).map(([skill, score]) => ({
                skill: skill.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                score: Math.round(score * 10), // Convert 0-1 to 0-100 scale
                level: score >= 0.8 ? 'Excellent' : score >= 0.6 ? 'Good' : score >= 0.4 ? 'Fair' : 'Needs Improvement'
              })) : [
                { skill: 'Communication', score: Math.round((data.overall_score || 0) * 0.9), level: 'Developing' },
                { skill: 'Technical Knowledge', score: Math.round((data.overall_score || 0) * 1.1), level: 'Developing' },
                { skill: 'Problem Solving', score: 75, level: 'Good' },
                { skill: 'Confidence', score: 80, level: 'Strong' }
              ],
              recommendations: evaluation.recommendations || [
                'Practice more behavioral questions using the STAR method',
                'Review technical concepts related to the role',
                'Work on providing measurable outcomes in your answers'
              ]
            });
          } else {
            // Use fallback data if API fails
            console.log('API failed, using fallback data');
            setReportData(getFallbackData());
          }
        } catch (error) {
          console.error('Error fetching report:', error);
          setReportData(getFallbackData());
        }
      } else {
        // No session ID, use fallback data
        console.log('No session ID, using fallback data');
        setReportData(getFallbackData());
      }
      setLoading(false);
    };

    fetchReport();
  }, [trainingData]);

  const getFallbackData = () => ({
    candidateName: trainingData.candidateName || 'Candidate',
    candidateEmail: trainingData.candidateEmail || '',
    jobTitle: trainingData.jobTitle || 'Position',
    duration: trainingData.duration || 5,
    completedAt: new Date().toLocaleString(),
    overallScore: 75,
    grade: 'B',
    questionsAsked: Math.floor((trainingData.duration || 5) / 2),
    strengths: [
      'Clear communication and articulation',
      'Good use of specific examples',
      'Professional demeanor throughout'
    ],
    areasForImprovement: [
      'Provide more quantifiable results in answers',
      'Structure responses using STAR method',
      'Expand on technical details when relevant'
    ],
    skillsAssessed: [
      { skill: 'Communication', score: 85, level: 'Strong' },
      { skill: 'Technical Knowledge', score: 70, level: 'Good' },
      { skill: 'Problem Solving', score: 75, level: 'Good' },
      { skill: 'Confidence', score: 80, level: 'Strong' }
    ],
    recommendations: [
      'Practice more behavioral questions using the STAR method',
      'Review technical concepts related to the role',
      'Work on providing measurable outcomes in your answers'
    ]
  });

  const handleDownloadReport = () => {
    // In production, this would download a PDF from the backend
    alert('Report download feature coming soon! A detailed PDF report has been sent to your email.');
  };

  const handleTakeAnotherSession = () => {
    navigate('/ai-trainer');
  };

  const handleReturnHome = () => {
    navigate('/');
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBackground = (score) => {
    if (score >= 80) return 'bg-green-100 dark:bg-green-900/20';
    if (score >= 70) return 'bg-blue-100 dark:bg-blue-900/20';
    if (score >= 60) return 'bg-yellow-100 dark:bg-yellow-900/20';
    return 'bg-red-100 dark:bg-red-900/20';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Generating Your Report...
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Our AI is analyzing your performance and preparing detailed feedback.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <Icon name="error" size={64} className="text-red-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Report Not Available
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              We couldn't load your training report. Please try again.
            </p>
            <Button onClick={() => navigate('/')}>Return Home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-blue-600 rounded-full mb-6">
            <Icon name="check_circle" size={40} className="text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            🎉 Training Session Complete!
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Great job, {reportData.candidateName}! Here's your detailed performance report.
          </p>
        </div>

        {/* Overall Score Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-6 mb-6">
              <div className={`text-7xl font-bold ${getScoreColor(reportData.overallScore)}`}>
                {reportData.overallScore}%
              </div>
              <div className="text-left">
                <div className="text-3xl font-bold text-gray-900 dark:text-white">
                  Grade: {reportData.grade}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-lg">
                  {reportData.questionsAsked} questions • {reportData.duration} minutes
                </div>
              </div>
            </div>
            
            {reportData.overallScore >= 80 ? (
              <p className="text-lg text-green-600 font-semibold">
                🌟 Excellent performance! You're interview-ready.
              </p>
            ) : reportData.overallScore >= 70 ? (
              <p className="text-lg text-blue-600 font-semibold">
                👍 Good job! A bit more practice will make you interview-ready.
              </p>
            ) : reportData.overallScore >= 60 ? (
              <p className="text-lg text-yellow-600 font-semibold">
                💪 Keep practicing! Focus on the areas for improvement below.
              </p>
            ) : (
              <p className="text-lg text-red-600 font-semibold">
                📚 More practice needed. Review the feedback and try again.
              </p>
            )}
          </div>

          {/* Session Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 p-6 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
            <div className="text-center">
              <Icon name="work" size={32} className="text-blue-600 mx-auto mb-2" />
              <div className="text-sm text-gray-600 dark:text-gray-400">Position</div>
              <div className="font-semibold text-gray-900 dark:text-white">{reportData.jobTitle}</div>
            </div>
            <div className="text-center">
              <Icon name="schedule" size={32} className="text-green-600 mx-auto mb-2" />
              <div className="text-sm text-gray-600 dark:text-gray-400">Duration</div>
              <div className="font-semibold text-gray-900 dark:text-white">{reportData.duration} minutes</div>
            </div>
            <div className="text-center">
              <Icon name="calendar_today" size={32} className="text-purple-600 mx-auto mb-2" />
              <div className="text-sm text-gray-600 dark:text-gray-400">Completed</div>
              <div className="font-semibold text-gray-900 dark:text-white text-sm">{reportData.completedAt}</div>
            </div>
          </div>

          {/* Download Button */}
          <div className="text-center">
            <Button
              size="lg"
              onClick={handleDownloadReport}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Icon name="download" size={20} className="mr-2" />
              Download Detailed PDF Report
            </Button>
            {reportData.candidateEmail && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                A copy has been sent to {reportData.candidateEmail}
              </p>
            )}
          </div>
        </div>

        {/* Skills Assessment */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <Icon name="assessment" size={28} className="text-blue-600" />
            Skills Assessment
          </h2>
          <div className="space-y-4">
            {reportData.skillsAssessed.map((skill, index) => (
              <div key={index}>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-gray-900 dark:text-white">{skill.skill}</span>
                  <span className={`font-bold ${getScoreColor(skill.score)}`}>
                    {skill.score}% - {skill.level}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      skill.score >= 80 ? 'bg-green-500' :
                      skill.score >= 70 ? 'bg-blue-500' :
                      skill.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${skill.score}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Strengths and Areas for Improvement */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Strengths */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
              <Icon name="star" size={28} className="text-green-600" />
              Your Strengths
            </h2>
            <ul className="space-y-4">
              {reportData.strengths.map((strength, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Icon name="check_circle" size={20} className="text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{strength}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Areas for Improvement */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
              <Icon name="trending_up" size={28} className="text-blue-600" />
              Areas to Improve
            </h2>
            <ul className="space-y-4">
              {reportData.areasForImprovement.map((area, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Icon name="arrow_forward" size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{area}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <Icon name="lightbulb" size={28} className="text-yellow-600" />
            Recommendations for Next Steps
          </h2>
          <ul className="space-y-4">
            {reportData.recommendations.map((recommendation, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="flex items-center justify-center w-6 h-6 bg-purple-600 text-white rounded-full text-sm font-bold flex-shrink-0">
                  {index + 1}
                </div>
                <span className="text-gray-700 dark:text-gray-300">{recommendation}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            size="lg"
            onClick={handleTakeAnotherSession}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
          >
            <Icon name="refresh" size={20} />
            Take Another Session
          </Button>
          
          <Button
            variant="outline"
            size="lg"
            onClick={handleReturnHome}
            className="flex items-center gap-2"
          >
            <Icon name="home" size={20} />
            Return Home
          </Button>
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-blue-100 dark:bg-blue-900/20 rounded-full">
            <Icon name="info" size={20} className="text-blue-600" />
            <span className="text-sm text-blue-700 dark:text-blue-300">
              Keep practicing to improve your interview skills! Consistency is key to success.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingReportPage;
